import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { Tab } from "@headlessui/react";
import { UserManagement } from "./UserManagement";
import { ModelManagement } from "./ModelManagement";
import { useUserPreferences } from "../contexts/UserPreferencesContext";
import { translations } from "../config/translations";

const AdminPanel = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const auth = useAuth();
  const { language } = useUserPreferences();
  const t = translations[language];

  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newModelName, setNewModelName] = useState("");
  const [newModelCommands, setNewModelCommands] = useState("");
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    group: "Users",
    quota: 0,
    assignedModels: [],
  });

  const USER_API_URL =
    "https://x0mhcopby7.execute-api.eu-central-1.amazonaws.com";
  const MODEL_API_URL =
    "https://fa2gzurc3l.execute-api.eu-central-1.amazonaws.com/admin/models";

  const fetchWithAuth = useCallback(
    async (url, options = {}) => {
      if (!auth.isAuthenticated) throw new Error("Not authenticated");

      const token = auth.user?.access_token;
      if (!token) throw new Error("No access token available");

      const defaultOptions = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      try {
        const response = await fetch(url, { ...defaultOptions, ...options });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }
        return response.json();
      } catch (error) {
        console.error("Fetch error:", error, "for URL:", url);
        throw error;
      }
    },
    [auth]
  );

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchWithAuth(
        `${USER_API_URL}/UserManagementFunction`
      );
      setUsers(data);
      setError(null);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(`Failed to fetch users: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  const fetchModels = useCallback(async () => {
    try {
      const data = await fetchWithAuth(MODEL_API_URL);
      setModels(Array.isArray(data) ? data : []);
    } catch (error) {
      setError(error.message);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchUsers();
      fetchModels();
    }
  }, [auth.isAuthenticated, fetchUsers, fetchModels]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000); // komunikat zniknie po 5 sekundach

      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleTabChange = (index) => {
    setSelectedTab(index);
    if (auth.isAuthenticated) {
      fetchUsers();
      fetchModels();
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    newUser.password = "noWOczeSN3%1!";
    // const passwordRegex =
    //   /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    // if (!passwordRegex.test(newUser.password)) {
    //   setError(t.passwordRequirements);
    //   setIsLoading(false);
    //   return;
    // }

    const formattedUser = {
      username: newUser.username,
      email: newUser.email,
      password: newUser.password,
      group: newUser.group,
      quota: parseInt(newUser.quota),
      assignedModels: newUser.assignedModels.map((model) =>
        typeof model === "string" ? { S: model } : model
      ),
    };

    try {
      await fetchWithAuth(`${USER_API_URL}/UserManagementFunction`, {
        method: "POST",
        body: JSON.stringify(formattedUser),
      });

      await fetchUsers();
      setNewUser({
        username: "",
        email: "",
        password: "",
        group: "Users",
        quota: 0,
        assignedModels: [],
      });
      setError("User created successfully!");
    } catch (error) {
      console.error("Create user error:", error);
      setError(`Failed to create user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      group: user.Role || "Users",
      quota: user.Quota || 0,
      assignedModels: user.AssignedModels || [],
    });
  };

  const handleSaveUser = async (username) => {
    if (!username || !editingUser) return;

    setIsLoading(true);
    setError(null);

    const updateData = {
      email: editingUser.email,
      role: editingUser.group,
      quota: parseInt(editingUser.quota) || 0,
      assignedModels: editingUser.assignedModels,
    };

    try {
      await fetchWithAuth(
        `${USER_API_URL}/UserManagementFunction/${encodeURIComponent(
          username
        )}`,
        {
          method: "PUT",
          body: JSON.stringify(updateData),
        }
      );

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.username === username
            ? { ...user, ...updateData, Role: updateData.role }
            : user
        )
      );

      setEditingUser(null);
      setError("User updated successfully!");
      fetchUsers();
    } catch (error) {
      setError("Failed to update user: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (
      !username ||
      !window.confirm("Are you sure you want to delete this user?")
    )
      return;

    setIsLoading(true);
    setError(null);

    try {
      await fetchWithAuth(
        `${USER_API_URL}/UserManagementFunction/${encodeURIComponent(
          username
        )}`,
        {
          method: "DELETE",
        }
      );

      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.username !== username)
      );
      setError("User deleted successfully!");
    } catch (error) {
      setError("Failed to delete user: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateModel = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await fetchWithAuth(MODEL_API_URL, {
        method: "POST",
        body: JSON.stringify({
          name: newModelName,
          commands: newModelCommands.split(",").map((cmd) => cmd.trim()),
        }),
      });

      await fetchModels();
      setNewModelName("");
      setNewModelCommands("");
      setError("Model created successfully!");
    } catch (error) {
      console.error("Create model error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveModel = async (model) => {
    setIsLoading(true);
    try {
      // Find all users who have this model assigned
      const affectedUsers = users.filter((user) =>
        user.AssignedModels?.some(
          (assignedModel) => assignedModel.S === model.originalName // Only check original name
        )
      );

      // First update the model itself
      const formattedModel = {
        name: model.name,
        commands: Array.isArray(model.commands)
          ? model.commands
          : typeof model.commands === "string"
          ? model.commands.split(",").map((cmd) => cmd.trim())
          : [],
      };

      await fetchWithAuth(`${MODEL_API_URL}/${model.modelId}`, {
        method: "PUT",
        body: JSON.stringify(formattedModel),
      });

      // Then update all affected users with the new model name
      await Promise.all(
        affectedUsers.map(async (user) => {
          // Create new array of assigned models with updated name
          const updatedModels = user.AssignedModels.map((assignedModel) => ({
            S:
              assignedModel.S === model.originalName
                ? model.name
                : assignedModel.S,
          }));

          await fetchWithAuth(
            `${USER_API_URL}/UserManagementFunction/${encodeURIComponent(
              user.username
            )}`,
            {
              method: "PUT",
              body: JSON.stringify({
                email: user.email,
                role: user.Role || "Users",
                quota: user.Quota || 0,
                assignedModels: updatedModels,
              }),
            }
          );
        })
      );

      // Fetch updated data
      await Promise.all([fetchModels(), fetchUsers()]);
      setEditingModel(null);
      setError(
        `Model updated successfully and updated for ${affectedUsers.length} users`
      );
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    const affectedUsers = users.filter((user) =>
      user.AssignedModels?.some(
        (model) => model.S === models.find((m) => m.modelId === modelId)?.name
      )
    );

    const modelName = models.find((m) => m.modelId === modelId)?.name;
    const message =
      `Are you sure you want to delete model "${modelName}"?\n\n` +
      `This model is currently assigned to ${affectedUsers.length} users.` +
      (affectedUsers.length > 0
        ? `\nAffected users: ${affectedUsers.map((u) => u.username).join(", ")}`
        : "");

    if (!window.confirm(message)) return;

    setIsLoading(true);
    try {
      await fetchWithAuth(`${MODEL_API_URL}/${modelId}`, {
        method: "DELETE",
      });

      for (const user of affectedUsers) {
        await fetchWithAuth(
          `${USER_API_URL}/UserManagementFunction/${encodeURIComponent(
            user.username
          )}`,
          {
            method: "PUT",
            body: JSON.stringify({
              email: user.email,
              role: user.Role || "Users",
              quota: user.Quota || 0,
              assignedModels: user.AssignedModels.filter(
                (model) =>
                  model.S !== models.find((m) => m.modelId === modelId)?.name
              ),
            }),
          }
        );
      }

      await Promise.all([fetchModels(), fetchUsers()]);
      setError("Model deleted successfully!");
    } catch (error) {
      console.error("Delete model error:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditModel = (model) => {
    setEditingModel({
      ...model,
      originalName: model.name, // Store the original name
      commands: Array.isArray(model.commands)
        ? model.commands.join(", ")
        : model.commands,
    });
  };

  const handleAssignModelToUsers = async (model, usernames) => {
    setIsLoading(true);
    setError(null);

    try {
      await Promise.all(
        usernames.map(async (username) => {
          const user = users.find((u) => u.username === username);
          if (!user) return;

          // Prepare the updated model assignments
          const currentModels = user.AssignedModels || [];
          const modelExists = currentModels.some((m) => m.S === model.name);

          if (!modelExists) {
            const updatedModels = [...currentModels, { S: model.name }];

            await fetchWithAuth(
              `${USER_API_URL}/UserManagementFunction/${encodeURIComponent(
                username
              )}`,
              {
                method: "PUT",
                body: JSON.stringify({
                  email: user.email,
                  role: user.Role || "Users",
                  quota: user.Quota || 0,
                  assignedModels: updatedModels,
                }),
              }
            );
          }
        })
      );

      await fetchUsers();
      setError(
        `Successfully assigned model "${model.name}" to ${usernames.length} users`
      );
    } catch (error) {
      setError(`Failed to assign model: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!auth.isAuthenticated) {
    return <div>Please sign in to access the admin panel.</div>;
  }

  return (
    <div className="p-4">
      {error && (
        <div
          className={`p-4 mb-4 rounded ${
            error.toLowerCase().includes(t.success.toLowerCase())
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-red-100 text-red-700 border border-red-200"
          }`}
        >
          {error}
        </div>
      )}

      <Tab.Group selectedIndex={selectedTab} onChange={handleTabChange}>
        <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-4">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
            ${
              selected
                ? "bg-white text-blue-700 shadow"
                : "text-blue-500 hover:bg-white/[0.12] hover:text-blue-600"
            }`
            }
          >
            {t.modelManagement}
            {selectedTab === 0 && selectedUsers.size > 0 && (
              <span className="ml-2">
                ({selectedUsers.size} {t.usersSelected})
              </span>
            )}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5 
            ${
              selected
                ? "bg-white text-blue-700 shadow"
                : "text-blue-500 hover:bg-white/[0.12] hover:text-blue-600"
            }`
            }
          >
            {t.userManagement}
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <ModelManagement
              models={models}
              selectedUsers={selectedUsers}
              isLoading={isLoading}
              editingModel={editingModel}
              newModelName={newModelName}
              newModelCommands={newModelCommands}
              handleCreateModel={handleCreateModel}
              handleEditModel={handleEditModel}
              handleSaveModel={handleSaveModel}
              handleDeleteModel={handleDeleteModel}
              handleAssignModelToUsers={handleAssignModelToUsers}
              setNewModelName={setNewModelName}
              setNewModelCommands={setNewModelCommands}
              setEditingModel={setEditingModel}
              language={language}
              translations={t}
              fetchWithAuth={fetchWithAuth}
            />
          </Tab.Panel>
          <Tab.Panel>
            <UserManagement
              users={users}
              models={models}
              isLoading={isLoading}
              editingUser={editingUser}
              newUser={newUser}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              handleCreateUser={handleCreateUser}
              handleEditUser={handleEditUser}
              handleSaveUser={handleSaveUser}
              handleDeleteUser={handleDeleteUser}
              setNewUser={setNewUser}
              setEditingUser={setEditingUser}
              fetchUsers={fetchUsers}
              fetchWithAuth={fetchWithAuth}
              setError={setError}
              language={language}
              translations={t}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default AdminPanel;
